/**
 * URL state shared by the paged lists (dataset list, model list): search text, sort, paging and
 * view. Each list adds its own fields and sort columns. A default is never written to the
 * address: no `sort` means the backend's default order.
 */

import { sortOrderSchema, type SortOrder } from '@contracts/common'

export type { SortOrder } from '@contracts/common'

export const LIST_VIEWS = ['table', 'card'] as const
export type ListView = (typeof LIST_VIEWS)[number]

export const PAGE_SIZE_OPTIONS = [10, 20, 50] as const
export const DEFAULT_PAGE_SIZE = 20
export const DEFAULT_VIEW: ListView = 'table'

export interface BaseListSearch<F extends string> {
  q?: string
  sort?: F
  order?: SortOrder
  page?: number
  pageSize?: number
  view?: ListView
}

function positiveInt(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value > 0 ? value : undefined
}

/** Tolerant parser: unknown or malformed values are dropped instead of failing the route. */
export function parseBaseListSearch<F extends string>(
  search: Record<string, unknown>,
  sortFields: readonly F[],
): BaseListSearch<F> {
  const sort = (sortFields as readonly unknown[]).includes(search.sort)
    ? (search.sort as F)
    : undefined
  const parsed = sortOrderSchema.safeParse(search.order)
  const order = parsed.success ? parsed.data : undefined
  const page = positiveInt(search.page)
  const pageSize = positiveInt(search.pageSize)
  // TanStack Router parses `?q=123` as a number.
  const q =
    typeof search.q === 'string' || typeof search.q === 'number' ? String(search.q).trim() : ''
  return {
    q: q === '' ? undefined : q,
    sort,
    // A direction means nothing without a column; a column without one is ascending.
    order: sort === undefined ? undefined : (order ?? 'asc'),
    page: page === 1 ? undefined : page,
    pageSize:
      pageSize !== undefined &&
      pageSize !== DEFAULT_PAGE_SIZE &&
      (PAGE_SIZE_OPTIONS as readonly number[]).includes(pageSize)
        ? pageSize
        : undefined,
    view: search.view === 'card' ? 'card' : undefined,
  }
}

/** What a paged list asks the backend for: the list state with its defaults applied. */
export interface BaseListParams<F extends string> {
  q: string | undefined
  /** Without `sort` the backend lists the newest first. */
  sort: F | undefined
  order: SortOrder | undefined
  page: number
  pageSize: number
}

/** The list state as request parameters: a column without a direction is ascending. */
export function toBaseListParams<F extends string>(search: BaseListSearch<F>): BaseListParams<F> {
  return {
    q: search.q,
    sort: search.sort,
    order: search.sort ? (search.order ?? 'asc') : undefined,
    page: search.page ?? 1,
    pageSize: search.pageSize ?? DEFAULT_PAGE_SIZE,
  }
}

/**
 * Sort state after activating a column header: another column → ascending; ascending →
 * descending; descending → the default order (no column sorted).
 */
export function nextSort<F extends string>(
  current: { sort?: F; order?: SortOrder },
  field: F,
): { sort: F | undefined; order: SortOrder | undefined } {
  if (current.sort !== field) return { sort: field, order: 'asc' }
  if ((current.order ?? 'asc') === 'asc') return { sort: field, order: 'desc' }
  return { sort: undefined, order: undefined }
}
